import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import VoiceRecorder from './VoiceRecorder';

function App() {
    const [messages, setMessages] = useState([]);
    const [currentMessage, setCurrentMessage] = useState('');
    const [currentUserId, setCurrentUserId] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [voiceResponseEnabled, setVoiceResponseEnabled] = useState(false);
    const [selectedVoice, setSelectedVoice] = useState('nova'); // Default to female voice
    const [isSpeaking, setIsSpeaking] = useState(false);
    const chatContainerRef = useRef(null);

    const [knowledgeBanks, setKnowledgeBanks] = useState([]);
    const [kbIdentifier, setKbIdentifier] = useState('main');
    const API_KEY = process.env.REACT_APP_CHATBOT_API_KEY || '';

    const hasArabicText = (text) => {
        const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
        return arabicRegex.test(text);
    };

    const getTextDirection = (text) => {
        return hasArabicText(text) ? 'rtl' : 'ltr';
    };

    const generateUserId = () => {
        const timestamp = Date.now();
        const random = Math.random().toString(36).substring(2, 8);
        return `user_${timestamp}_${random}`;
    };

    const USER_ID_KEY = 'chatbot_user_id';
    const MESSAGES_KEY = 'chatbot_messages';

    const saveToLocalStorage = (userId, messages) => {
        try {
            localStorage.setItem(USER_ID_KEY, userId);
            localStorage.setItem(MESSAGES_KEY, JSON.stringify(messages));
        } catch (error) {
            console.error('Failed to save to localStorage:', error);
        }
    };

    const loadFromLocalStorage = () => {
        try {
            const savedUserId = localStorage.getItem(USER_ID_KEY);
            const savedMessages = localStorage.getItem(MESSAGES_KEY);

            return {
                userId: savedUserId,
                messages: savedMessages ? JSON.parse(savedMessages) : null
            };
        } catch (error) {
            console.error('Failed to load from localStorage:', error);
            return { userId: null, messages: null };
        }
    };

    const loadConversationFromBackend = async (userId) => {
        try {
            const clinicId = 'dev';
            const params = new URLSearchParams({ clinic_id: clinicId, user_id: userId, kb_identifier: kbIdentifier });
            const response = await fetch(`/documents/conversation/${clinicId}/${userId}?${params.toString()}`, {
                headers: { 'X-API-Key': API_KEY }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.messages && data.messages.length > 0) {
                    const convertedMessages = [];

                    data.messages.forEach((msg, index) => {
                        convertedMessages.push({
                            id: `user_${index}_${Date.now()}`,
                            sender: 'user',
                            text: msg.user_message,
                            timestamp: new Date(msg.timestamp).getTime()
                        });

                        convertedMessages.push({
                            id: `bot_${index}_${Date.now()}`,
                            sender: 'bot',
                            text: msg.bot_response,
                            timestamp: new Date(msg.timestamp).getTime() + 1
                        });
                    });

                    return convertedMessages;
                }
            }
        } catch (error) {
            console.error('Failed to load conversation from backend:', error);
        }
        return null;
    };

    const clearConversationFromBackend = async (userId) => {
        try {
            const clinicId = 'dev';
            const params = new URLSearchParams({ clinic_id: clinicId, user_id: userId, kb_identifier: kbIdentifier });
            await fetch(`/documents/conversation/${clinicId}/${userId}?${params.toString()}`, {
                method: 'DELETE',
                headers: {
                    'X-API-Key': process.env.REACT_APP_CHATBOT_API_KEY
                }
            });
        } catch (error) {
            console.error('Failed to clear conversation from backend:', error);
        }
    };

    const copyMessage = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    };

    const formatTime = (timestamp) => {
        return new Date(timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const clearLocalStorage = () => {
        try {
            localStorage.removeItem(USER_ID_KEY);
            localStorage.removeItem(MESSAGES_KEY);
        } catch (error) {
            console.error('Failed to clear localStorage:', error);
        }
    };

    const speakText = (text) => {
        if (!voiceResponseEnabled || !text || isSpeaking) return;

        // Check if speech synthesis is supported
        if (!('speechSynthesis' in window)) {
            console.warn('Speech synthesis not supported in this browser');
            return;
        }

        // Cancel any ongoing speech
        window.speechSynthesis.cancel();

        const utterance = new SpeechSynthesisUtterance(text);

        // Configure voice settings
        utterance.rate = 1;
        utterance.pitch = 1;
        utterance.volume = 0.8;

        // Try to use Arabic voice if text contains Arabic characters
        if (hasArabicText(text)) {
            const voices = window.speechSynthesis.getVoices();
            const arabicVoice = voices.find(voice =>
                voice.lang.includes('ar') || voice.name.includes('Arabic')
            );
            if (arabicVoice) {
                utterance.voice = arabicVoice;
            }
        }

        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = () => setIsSpeaking(false);

        window.speechSynthesis.speak(utterance);
    };

    const stopAudio = () => {
        setIsSpeaking(false);

        const audioElements = document.querySelectorAll('audio');
        audioElements.forEach(audio => {
            audio.pause();
            audio.currentTime = 0;
        });
    }; const playAudioResponse = async (audioData) => {
        if (!audioData || isSpeaking) return;

        try {
            setIsSpeaking(true);

            if (audioData && audioData.audio_data) {
                const byteCharacters = atob(audioData.audio_data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const audioBlob = new Blob([byteArray], { type: 'audio/mp3' });
                const audioUrl = URL.createObjectURL(audioBlob);

                const audio = new Audio(audioUrl);

                audio.onended = () => {
                    URL.revokeObjectURL(audioUrl); // Clean up blob URL
                    setIsSpeaking(false);
                };

                audio.onerror = (error) => {
                    URL.revokeObjectURL(audioUrl); // Clean up blob URL
                    setIsSpeaking(false);
                    const lastMessage = messages[messages.length - 1];
                    if (lastMessage && lastMessage.text) {
                        speakText(lastMessage.text);
                    }
                };

                await audio.play();
            } else {
                setIsSpeaking(false);
            }
        } catch (error) {
            setIsSpeaking(false);
        }
    };

    useEffect(() => {
        const savedData = loadFromLocalStorage();

        const initializeConversation = async () => {
            if (savedData.userId && savedData.messages && savedData.messages.length > 0) {
                setCurrentUserId(savedData.userId);
                setMessages(savedData.messages);
            } else if (savedData.userId) {
                const backendMessages = await loadConversationFromBackend(savedData.userId);
                if (backendMessages && backendMessages.length > 0) {
                    setCurrentUserId(savedData.userId);
                    setMessages(backendMessages);
                    saveToLocalStorage(savedData.userId, backendMessages);
                } else {
                    createNewChat();
                }
            } else {
                createNewChat();
            }
        };

        initializeConversation();
    }, []);

    useEffect(() => {
        const fetchKbs = async () => {
            try {
                const clinicId = 'dev';
                const res = await fetch(`/kb/list?clinic_id=${clinicId}`, {

                    headers: { 'X-API-Key': API_KEY }
                });
                if (res.ok) {
                    const data = await res.json();
                    const kbs = data.knowledge_banks || [];
                    setKnowledgeBanks(kbs);
                    if (kbs.length > 0) {
                        const savedKb = localStorage.getItem('chatbot_kb') || null;
                        const initial = savedKb && kbs.find(k => k.kb_identifier === savedKb) ? savedKb : kbs[0].kb_identifier;
                        setKbIdentifier(initial);
                    }
                }
            } catch (e) {
                console.error('Failed to fetch knowledge banks', e);
            }
        };
        fetchKbs();
    }, []);

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages]);

    useEffect(() => {
        if ('speechSynthesis' in window) {
            const loadVoices = () => {
                const voices = window.speechSynthesis.getVoices();
            };

            loadVoices();
            if (window.speechSynthesis.onvoiceschanged !== undefined) {
                window.speechSynthesis.onvoiceschanged = loadVoices;
            }
        }
    }, []);

    useEffect(() => {
        if (currentUserId && messages.length > 0) {
            saveToLocalStorage(currentUserId, messages);
        }
    }, [messages, currentUserId]);

    const createNewChat = async () => {
        if (currentUserId) {
            await clearConversationFromBackend(currentUserId);
        }

        const newUserId = generateUserId();
        const welcomeMessages = [];

        setCurrentUserId(newUserId);
        setMessages(welcomeMessages);

        clearLocalStorage();
        saveToLocalStorage(newUserId, welcomeMessages);
    };

    const handleMessageChange = (e) => {
        setCurrentMessage(e.target.value);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            sendMessage();
        }
    };

    const sendMessage = async () => {
        const message = currentMessage.trim();

        if (!message) {
            alert('Please enter a message!');
            return;
        }

        if (!currentUserId) {
            createNewChat();
        }

        const userMessage = {
            id: Date.now(),
            sender: 'user',
            text: message,
            timestamp: Date.now()
        };

        setMessages(prev => [...prev, userMessage]);
        setCurrentMessage('');
        setIsLoading(true);

        try {
            const apiUrl = '/documents/ask';
            const clinicId = 'dev';
            const params = new URLSearchParams({
                clinic_id: clinicId,
                query: message,
                user_id: currentUserId,
                kb_identifier: kbIdentifier,
                voice_response_enabled: voiceResponseEnabled,
                voice: selectedVoice
            });

            const fullUrl = `${apiUrl}?${params}`;

            const response = await fetch(fullUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-API-Key': API_KEY
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            let botMessageText;
            let audioData = null;

            if (data.answer) {
                botMessageText = data.answer;
                audioData = data.voice_data || null;
            } else {
                botMessageText = 'Sorry, I could not find an answer to your question. Please try rephrasing or contact our support team.';
            }

            const botMessage = {
                id: Date.now() + 1,
                sender: 'bot',
                text: botMessageText,
                timestamp: Date.now(),
                audioUrl: audioData,
                isVoiceResponse: data.is_voice_response || false
            };

            setMessages(prev => [...prev, botMessage]);

            if (voiceResponseEnabled) {
                if (audioData) {
                    playAudioResponse(audioData);
                } else {
                    speakText(botMessageText);
                }
            }

        } catch (error) {
            const errorMessage = {
                id: Date.now() + 1,
                sender: 'bot',
                text: `❌ Error: ${error.message}. Please check if the API server is running and the URL is correct.`,
                timestamp: Date.now()
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const sendVoiceMessage = async (audioBlob) => {
        if (!currentUserId) {
            createNewChat();
        }

        const userMessage = {
            id: Date.now(),
            sender: 'user',
            text: '🎤 Voice message',
            type: 'voice',
            audioBlob: audioBlob,
            timestamp: Date.now()
        };

        setMessages(prev => [...prev, userMessage]);
        setIsLoading(true);

        try {
            const formData = new FormData();
            formData.append('audio_file', audioBlob, 'voice_message.webm');

            const apiUrl = '/documents/voice-query';
            const clinicId = 'dev';
            const params = new URLSearchParams({
                clinic_id: clinicId,
                user_id: currentUserId,
                kb_identifier: kbIdentifier,
                voice_response_enabled: voiceResponseEnabled,
                voice: selectedVoice
            });

            const response = await fetch(`${apiUrl}?${params}`, {
                method: 'POST',
                headers: {
                    'X-API-Key': process.env.REACT_APP_CHATBOT_API_KEY
                },
                body: formData
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            setMessages(prev => prev.map(msg =>
                msg.id === userMessage.id
                    ? { ...msg, text: data.transcribed_text || '🎤 Voice message', transcription: data.transcribed_text }
                    : msg
            ));

            let botMessageText = data.bot_response || data.answer || 'Sorry, I could not process your voice message.';
            let audioData = data.voice_data || null;

            const botMessage = {
                id: Date.now() + 1,
                sender: 'bot',
                text: botMessageText,
                timestamp: Date.now(),
                audioUrl: audioData,
                isVoiceResponse: data.is_voice_response || false
            };

            setMessages(prev => [...prev, botMessage]);

            if (voiceResponseEnabled) {
                if (audioData) {
                    playAudioResponse(audioData);
                } else {
                    speakText(botMessageText);
                }
            }

        } catch (error) {
            const errorMessage = {
                id: Date.now() + 1,
                sender: 'bot',
                text: `❌ Voice Error: ${error.message}. Please check if voice services are enabled.`,
                timestamp: Date.now()
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="container">
            <div className="header">
                <h1>🤖 Chatbot</h1>
                <p>Ask me anything in any language - I'll detect and respond automatically!</p>
            </div>

            <div className="controls">
                <div className="control-group">
                    <button
                        className="new-chat-btn"
                        onClick={createNewChat}
                        disabled={isLoading}
                        title="Start a new conversation (current chat will be saved)"
                    >
                        🆕 New Chat
                    </button>
                    {currentUserId && (
                        <div className="user-info">
                            <small>Session: {currentUserId.substring(5, 15)}...</small>
                        </div>
                    )}
                    <div className="kb-selector">
                        <label htmlFor="kb-select">Knowledge Bank:</label>
                        <select
                            id="kb-select"
                            value={kbIdentifier}
                            onChange={(e) => {
                                setKbIdentifier(e.target.value);
                                localStorage.setItem('chatbot_kb', e.target.value);
                            }}
                        >
                            {knowledgeBanks.map((kb) => (
                                <option key={kb.kb_identifier} value={kb.kb_identifier}>{kb.kb_name || kb.kb_identifier}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="voice-controls">
                    <div className="response-mode-selector">
                        <button
                            className={`mode-btn ${!voiceResponseEnabled ? 'active' : ''}`}
                            onClick={() => {
                                setVoiceResponseEnabled(false);
                            }}
                            disabled={isLoading}
                        >
                            📝 Text Response
                        </button>
                        <button
                            className={`mode-btn ${voiceResponseEnabled ? 'active' : ''}`}
                            onClick={() => {
                                setVoiceResponseEnabled(true);
                            }}
                            disabled={isLoading}
                        >
                            🔊 Voice Response
                        </button>
                    </div>
                    {voiceResponseEnabled && (
                        <div className="voice-selection">
                            <label htmlFor="voice-select">🎤 Voice:</label>
                            <select
                                id="voice-select"
                                value={selectedVoice}
                                onChange={(e) => {
                                    setSelectedVoice(e.target.value);
                                }}
                                className="voice-dropdown"
                            >
                                <option value="alloy">Alloy (Neutral)</option>
                                <option value="echo">Echo (Male)</option>
                                <option value="fable">Fable (British Male)</option>
                                <option value="onyx">Onyx (Deep Male)</option>
                                <option value="nova">Nova (Female) 👩</option>
                                <option value="shimmer">Shimmer (Soft Female) 👩</option>
                                <option value="ar-AE-FatimaNeural">Fatima (UAE Arabic) 🇦🇪👩</option>
                                <option value="ar-SA-ZariyahNeural">Zariyah (Saudi Arabic) 🇸🇦👩</option>
                                <option value="ar-KW-NouraNeural">Noura (Kuwait Arabic) 🇰🇼👩</option>
                            </select>
                        </div>
                    )}
                    {isSpeaking && (
                        <button
                            className="stop-speaking-btn"
                            onClick={stopAudio}
                            title="Stop current audio playback"
                        >
                            🔇 Stop Audio
                        </button>
                    )}
                </div>
            </div>

            <div className="chat-container" ref={chatContainerRef}>
                {messages.map((message) => {
                    const textDirection = getTextDirection(message.text);
                    const messageClasses = `message ${message.sender === 'user' ? 'user-message' : 'bot-message'} ${textDirection} ${hasArabicText(message.text) ? 'arabic-text' : ''}`;

                    return (
                        <div
                            key={message.id}
                            className={messageClasses}
                            style={{
                                direction: textDirection,
                                textAlign: textDirection === 'rtl' ? 'right' : 'left'
                            }}
                        >
                            <div className="message-header">
                                <strong>
                                    {message.sender === 'user' ? '👤 You' : '🤖 Chatbot'}
                                    {message.sender === 'bot' && voiceResponseEnabled && (
                                        <span className="voice-indicator" title="Voice response enabled">
                                            🔊
                                        </span>
                                    )}
                                </strong>
                                {message.timestamp && (
                                    <span className="timestamp">{formatTime(message.timestamp)}</span>
                                )}
                            </div>
                            <div
                                className="message-text"
                                style={{
                                    direction: textDirection,
                                    textAlign: textDirection === 'rtl' ? 'right' : 'left'
                                }}
                            >
                                {message.text}
                                {message.type === 'voice' && message.transcription && (
                                    <div className="transcription">
                                        <small>📝 {message.transcription}</small>
                                    </div>
                                )}
                                {message.isVoiceResponse && (
                                    <div className="audio-response-indicator">
                                        <small>🎵 Audio response available</small>
                                    </div>
                                )}
                            </div>
                            <div className="message-actions">
                                <button
                                    className="copy-btn"
                                    onClick={() => copyMessage(message.text)}
                                    title="Copy message"
                                >
                                    📋
                                </button>
                                {message.isVoiceResponse && message.audioUrl && (
                                    <button
                                        className="replay-btn"
                                        onClick={() => playAudioResponse(message.audioUrl)}
                                        title="Replay audio"
                                        disabled={isSpeaking}
                                    >
                                        🔄 Replay
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {isLoading && (
                <div className="loading show">
                    <div>🔄 Processing your request...</div>
                </div>
            )}

            <div className="input-area">
                <div className="input-group">
                    <input
                        type="text"
                        className={`message-input ${getTextDirection(currentMessage)}`}
                        placeholder="Type your question here..."
                        value={currentMessage}
                        onChange={handleMessageChange}
                        onKeyPress={handleKeyPress}
                        style={{
                            direction: getTextDirection(currentMessage),
                            textAlign: getTextDirection(currentMessage) === 'rtl' ? 'right' : 'left'
                        }}
                    />
                    <VoiceRecorder
                        onVoiceMessage={sendVoiceMessage}
                        isLoading={isLoading}
                    />
                    <button
                        className="send-btn"
                        onClick={sendMessage}
                        disabled={isLoading}
                    >
                        Send 📤
                    </button>
                </div>
            </div>
        </div>
    );
}

export default App;
